import { Drawable } from "./drawable";
import Cursor from "./cursor";
import { ScreenState } from "./screen";
import { SVG } from "./image";
import { rgb, rgba } from "../utils/color";

/**
 * Checks if the center of the circle is intersecting a rectangle.
 * @param {number} center_x The x coordinate of the center of the circle
 * @param {number} center_y The y coordinate of the center of the circle
 * @param {number} rect_x The x coordinate of the rectangle
 * @param {number} rect_y The y coordinate of the rectangle
 * @param {number} rect_w The width of the rectangle
 * @param {number} rect_h The height of the rectangle
 * @returns {Boolean} True if the circle center is intersecting the rectangle.
 */
function __check_center(center_x, center_y, rect_x, rect_y, rect_w, rect_h){
  return Boolean(
    (center_x >= rect_x && center_x <= (rect_x + rect_w)) &&
    (center_y >= rect_y && center_y <= (rect_y + rect_h))
  )
}


/**
 * Checks if the circle is intersecting the rectangle based on a side of the rectangle.
 * @param {number} center_x The x coordinate of the center of the circle
 * @param {number} center_y The y coordinate of the center of the circle
 * @param {number} radius The radius of the circle
 * @param {number} rect_x1 The first x coordinate of side of the rectangle
 * @param {number} rect_y1 The first y coordinate of side of the rectangle
 * @param {number} rect_x2 The second x coordinate of side of the rectangle
 * @param {number} rect_y2 The second x coordinate of side of the rectangle
 * @returns {Boolean} True if the shortest distance between the cricle center 
 * and a side of a rectangle is less than the circle's radius.
 * (i.e., the circle is intersecting the rectangle.)
 */
function __check_distance(center_x, center_y, radius, rect_x1, rect_y1, rect_x2, rect_y2){
  // TODO: This algorithim is not working. 

  // Step 1: Create equation of line (y=mx+c) from 2 coordinates.
  // m = (y2-y1)/(x2-x1)
  // y=mx+c
  // c=y-mx
  const m = (rect_y2-rect_y1)/(rect_x2-rect_x1);
  const c = rect_y1 - m*rect_x1;

  // Step 2: Create a new line, perpendicular to the first line, but still
  // intersecting the circle's center
  // y2=(m2)(x2)+c2
  // m2 = -1/m
  // c2=y2-(m2)(x2)
  const m2 = -1/m; // TODO: Possible division by zero error!
  const c2 = center_y - m2*center_x;


  // Step 3: Solve the simultaneuos equations (y=mx+c and y2=(m2)(x2)+c2) 
  // to check where the 2 lines intersect.
  // 
  // let (x3, y3) be the coordinates at which the to lines intersect. 
  // m(x3)+c = (m2)(x3) + c2
  // (m - m2)(x3) = c2 - c
  // x3 = (c2-c)/(m-m2)
  // y3 = m(x3) + c = (m2)(x3)+c2
  const x3 = (c2-c)/(m-m2);
  const y3 = m*x3 + c;

  // Step 4: Check if the distance between the point of intersection is smaller than 
  // the circle's radius.
  // distance = √((x3-center_x)^2+(y3-center_y)^2)
  return Boolean(Math.sqrt((x3-center_x)**2 + (y3-center_y)**2) <= radius);
}

/**
 * Checks if 2 circles are intersecting each other.
 * @param {number} center_x The x coordinate of the first circle
 * @param {number} center_y The y coordinate of the first circle
 * @param {number} radius The radius coordinate of the first circle
 * @param {number} center_x2 The x coordinate of the second circle
 * @param {number} center_y2 The y coordinate of the second circle
 * @param {number} radius_2 The radius coordinate of the second circle
 * @returns {Boolean} True if the 2 circles are colliding with each other
 */
export function __check_circle_collision(center_x, center_y, radius, center_x2, center_y2, radius_2){
  const dist_between_cricles = Math.sqrt((center_x2-center_x)**2 + (center_y2-center_y)**2)
  const collision_distance = radius + radius_2
  return dist_between_cricles < collision_distance;
}

export class ButtonBase extends Drawable{
  width = 0;
  height = 0;
  hover_progress = 0; /** Ranges from 0 to 1 */
  hover_duration = 2300; /** Time required to register button as clicked. */
  unhover_duration = 1000; /** Time for animation to unhover button. */
  first_hover_timestamp = /** @type {number | null} */ (null); /** Timestamp when hover */
  stop_hover_timestamp = /** @type {number | null} */ (null); /** Timestamp when hover */
  clickHandlerRan = false; /** Whether the click handler has ran. */
  onClick = /** @type {(cursor: ButtonBase) => any | void} */  ((cursor) => {});
   /**
  * Initialiases the drawable.
  * @param {number} x A positive number indicating x coordinate on screen
  * @param {number} y A positive number indicating y coordinate on screen
  * @param {number} width The width of the button.
  * @param {number} height The height of the button.
  * @param {(cursor: ButtonBase) => any | void} onClick The button's click handler.
  * @param {boolean} [configureCanvas=true] Whether to configure canvas before render
  */
  constructor(x, y, width, height, onClick, configureCanvas){
    super(x, y, configureCanvas);
    this.width = width;
    this.height = height;
    this.onClick = onClick;
  }

  /**
   * Checks if cursors are intersecting object.
   * @param {Cursor[]} cursors A list of cursors to check
   * @returns {Boolean} true if there are cursors intersecting the object.
   */
  checkHover(cursors){
    for (let i = 0; i < cursors.length; i++){
      if (
        __check_center(cursors[i].x, cursors[i].y, this.x, this.y, this.width, this.height) || 
        __check_distance(cursors[i].x, cursors[i].y, cursors[i].radius, this.x, this.y, (this.x + this.width), this.y) ||
        __check_distance(cursors[i].x, cursors[i].y, cursors[i].radius, (this.x + this.width), this.y, (this.x + this.width), (this.y + this.height)) ||
        __check_distance(cursors[i].x, cursors[i].y, cursors[i].radius, (this.x + this.width), (this.y + this.height), this.x, (this.y + this.height)) ||
        __check_distance(cursors[i].x, cursors[i].y, cursors[i].radius, this.x, (this.y + this.height), this.x, this.y)
      ){
        return true;
      }
    }
    return false;
  }

  /**
   * Update the object's state based on the state of the cursor.
   * @param {Cursor[]} cursors The list of cursor objects to check if the button is colliding with the cursor. 
   * @param {number} animationStep A positive number indicating current animation timeline
   */
  updateHover(cursors, animationStep){
    //Referecne: https://stackoverflow.com/a/402019/18573662
    // console.log(__check_distance(cursor.x, cursor.y, cursor.radius, this.x, this.y, (this.x + this.width), this.y))
    if (
      this.checkHover(cursors)
    ){
      this.stop_hover_timestamp = null;
      if (!this.first_hover_timestamp){
        this.first_hover_timestamp = animationStep;
      }
      this.hover_progress = Math.min((animationStep - this.first_hover_timestamp)/this.hover_duration, 1);

      if (this.hover_progress >= 1 && !this.clickHandlerRan){
        this.onClick(this)
        this.clickHandlerRan = true;
      }
      return true;
    }
    else{
      this.first_hover_timestamp = null;
      if (!this.stop_hover_timestamp){
        this.stop_hover_timestamp = animationStep;
      }
      this.hover_progress = Math.max(1 - (animationStep - this.stop_hover_timestamp)/this.unhover_duration, 0);

      if (this.hover_progress < 1 && this.clickHandlerRan){
        this.clickHandlerRan = false;
      }
      return false;
    }
  }
}

export class RectButton extends ButtonBase{
  /**
  * Initialiases the Rect Button Drawable.
  * @param {number} x A positive number indicating x coordinate on screen
  * @param {number} y A positive number indicating y coordinate on screen
  * @param {number} width The width of the button.
  * @param {number} height The height of the button.
  * @param {string} text The text to render.
  * @param {(cursor: Cursor) => any | void} onClick The button's click handler.
  * @param {number | DOMPointInit | (number | DOMPointInit)[]} border_radius The border radius of the rectangle
  * @param {boolean} [configureCanvas=true] Whether to configure canvas before render
  * @param {number} [fontSize=48] The font size
  * @param {string} [font='Yusei Magic'] The text font
  */
  constructor(x, y, width, height, text, onClick, border_radius, configureCanvas, fontSize, font){
    super(x, y, width, height, onClick, configureCanvas);
    this.text = text;
    this.fontSize = fontSize || 48;
    this.font = font || 'Yusei Magic';
    this.border_radius = border_radius || 10;
  }

  /**
  * Configures the canvas to render the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  configure(screenState){
    screenState.canvasCtx.font = `${screenState.S(this.fontSize*screenState.transitionProgress)}px ${this.font}`;
    screenState.canvasCtx.textAlign = 'center';
    screenState.canvasCtx.textBaseline = 'middle';
  }

  /**
  * Renders the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  render(screenState) {
    super.render(screenState);
    screenState.canvasCtx.fillText(this.text, screenState.X(this.x + this.width/2), screenState.Y(this.y + this.height/2));
    screenState.canvasCtx.beginPath();
    screenState.canvasCtx.roundRect(screenState.X(this.x), screenState.Y(this.y), screenState.X(this.width*screenState.transitionProgress), screenState.Y(this.height*screenState.transitionProgress), screenState.S(this.border_radius));
    screenState.canvasCtx.fill();
  }
}



export class CircularButtonBase extends Drawable{
  radius = 0;
  hover_progress = 0; /** Ranges from 0 to 1 */
  hover_duration = 2300; /** Time required to register button as clicked. */
  unhover_duration = 1000; /** Time for animation to unhover button. */
  first_hover_timestamp = /** @type {number | null} */ (null); /** Timestamp when hover */
  stop_hover_timestamp = /** @type {number | null} */ (null); /** Timestamp when hover */
  clickHandlerRan = false; /** Whether the click handler has ran. (i.e. whether the button has been clicked.*/
  onClick = /** @type {(button: CircularButton) => any | void} */  ((button) => {});
  disabled = false;
   /**
  * Initialiases the drawable.
  * @param {number} x A positive number indicating x coordinate on screen
  * @param {number} y A positive number indicating y coordinate on screen
  * @param {number} radius The radius of the circle.
  * @param {(cursor: CircularButton) => any | void} onClick The button's click handler.
  * @param {boolean} [configureCanvas=true] Whether to configure canvas before render
  */
  constructor(x, y, radius, onClick, configureCanvas){
    super(x, y, configureCanvas);
    this.radius = radius;
    this.onClick = onClick;
  }

  /**
   * Checks if cursors are intersecting object.
   * @param {Cursor[]} cursors A list of cursors to check
   * @param {ScreenState} screenState The current state of the screen
   * @returns {number | undefined} index of the first cursor that is intersecting the object.
   */
  checkHover(cursors, screenState){
    const [newX, newY, newRadius] = [screenState.X(this.x), screenState.Y(this.y), screenState.S(this.radius)];
    for (let i = 0; i < cursors.length; i++){
      if (__check_circle_collision(cursors[i].x, cursors[i].y, cursors[i].radius, newX, newY, newRadius)){
        return i;
      }
    }
  }

  /**
   * Update the object's state based on the state of the cursor.
   * @param {Cursor[]} cursors The list of cursor objects to check if the button is colliding with the cursor. 
   * @param {ScreenState} screenState The current state of the screen
   */
  updateHover(cursors, screenState){
    const animationStep = screenState.animationStep
    const hover_cursor_index = this.checkHover(cursors, screenState)
    if (
      !this.disabled && hover_cursor_index != undefined
    ){
      if(cursors[hover_cursor_index].type == "touch"){//This is a temporary fix
        this.hover_duration = 500;
      }
      if (!this.first_hover_timestamp){
        this.first_hover_timestamp = animationStep - (this.stop_hover_timestamp ? ((this.hover_progress)*this.hover_duration) : 0);
      }
      this.stop_hover_timestamp = null;
      this.hover_progress = Math.min((animationStep - this.first_hover_timestamp)/this.hover_duration, 1);

      if (this.hover_progress >= 1 && !this.clickHandlerRan){
        this.onClick(this);
        this.clickHandlerRan = true;
      }
      return true;
    }
    else{
      if (!this.stop_hover_timestamp){
        this.stop_hover_timestamp = animationStep - (this.first_hover_timestamp ? ((1-this.hover_progress)*this.unhover_duration) : this.unhover_duration);
        this.hover_duration = 2300;//This is a temporary fix
      }
      this.first_hover_timestamp = null;
      this.hover_progress = Math.max(1 - (animationStep - this.stop_hover_timestamp)/this.unhover_duration, 0);
 
      if (this.hover_progress < 1 && this.clickHandlerRan){
        this.clickHandlerRan = false;
      }
      return false;
    }
  }
}



export class CircularButton extends CircularButtonBase{
  /**
  * Initialiases the  drawable.
  * @param {number} x A positive number indicating x coordinate on screen
  * @param {number} y A positive number indicating y coordinate on screen 
  * @param {number} radius The radius of the circle.
  * @param {string} text The text to render.
  * @param {(button: CircularButton) => any | void} onClick The button's click handler.
  * @param {boolean} [configureCanvas=true] Whether to configure canvas before render
  * @param {number} [fontSize=28] The font size
  * @param {string} [font='Yusei Magic'] The text font
  */
  constructor(x, y, radius, text, onClick, configureCanvas, fontSize, font){
    super(x, y, radius, onClick, configureCanvas);
    this.text = text;
    this.fontSize = fontSize || 28;
    this.font = font || 'Yusei Magic';
  }

  /**
  * Configures the canvas to render the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  configure(screenState){
    const transitionProgress = this.overrideTransitionProgress != undefined ? this.overrideTransitionProgress : screenState.transitionProgress;
    screenState.canvasCtx.font = `${screenState.S(this.fontSize*transitionProgress)}px ${this.font}`;
    screenState.canvasCtx.textAlign = 'center';
    screenState.canvasCtx.textBaseline = 'middle';
  }

  /**
  * Renders the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  render(screenState) {
    super.render(screenState);
    const transitionProgress = this.overrideTransitionProgress != undefined ? this.overrideTransitionProgress : screenState.transitionProgress;
    const xPos = this.overrideXpos != undefined ? this.overrideXpos : screenState.X(this.x);
    const yPos =  this.overrideYpos != undefined ? this.overrideYpos : screenState.Y(this.y);
    const radius = screenState.S(this.radius*transitionProgress);
    const inner_radius = screenState.S((this.radius-2)*transitionProgress);
    screenState.canvasCtx.beginPath();
    screenState.canvasCtx.arc(xPos, yPos, radius, 0,2*Math.PI);
    screenState.canvasCtx.fillStyle = this.hover_progress > 0 ? rgb(108, 212, 255) : rgb(15,223,23);
    screenState.canvasCtx.fill();

    screenState.canvasCtx.setLineDash([screenState.S(8), screenState.S(4)]);
    screenState.canvasCtx.lineDashOffset = -(screenState.animationStep/50 % screenState.S(12));
    screenState.canvasCtx.lineWidth = screenState.S(3);
    screenState.canvasCtx.beginPath();
    screenState.canvasCtx.arc(xPos, yPos, inner_radius, 0,2*Math.PI);
    // screenState.canvasCtx.strokeStyle = 'blue'
    screenState.canvasCtx.shadowBlur = 15;
    screenState.canvasCtx.shadowColor = rgb(228, 255, 26);
    screenState.canvasCtx.strokeStyle = rgb(228, 255, 26);
    screenState.canvasCtx.stroke();
    screenState.canvasCtx.shadowBlur = 0;

    screenState.canvasCtx.setLineDash([]);
    screenState.canvasCtx.lineDashOffset = 0;
    // screenState.canvasCtx.lineWidth = 3;
    screenState.canvasCtx.beginPath();
    screenState.canvasCtx.arc(xPos, yPos, inner_radius, -Math.PI/2,(2*Math.PI*this.hover_progress)-Math.PI/2);
    // screenState.canvasCtx.strokeStyle = rgb(228, 255, 26);
    screenState.canvasCtx.stroke()

    screenState.canvasCtx.fillStyle = rgb(228, 255, 26);
    screenState.canvasCtx.fillText(this.text, xPos, yPos);
  }
}












export class IconButton extends CircularButtonBase{
  loading = false;

  /**
  * Initialiases the drawable.
  * @param {number} x A positive number indicating x coordinate on screen
  * @param {number} y A positive number indicating y coordinate on screen 
  * @param {number} radius The radius of the circle.
  * @param {SVG} icon The SVG Icon to render.
  * @param {string} text The text to render.
  * @param {(button: CircularButton) => any | void} onClick The button's click handler.
  * @param {boolean} [configureCanvas=true] Whether to configure canvas before render
  * @param {number} [fontSize=21] The font size
  * @param {string} [font='Yusei Magic'] The text font
  */
  constructor(x, y, radius, icon, text, onClick, configureCanvas, fontSize, font){
    super(x, y, radius, onClick, configureCanvas);
    this.icon = icon;
    this.text = text;
    this.fontSize = fontSize || 21;
    this.font = font || 'Yusei Magic';
  }

  /**
  * Configures the canvas to render the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  configure(screenState){
    screenState.canvasCtx.font = `${screenState.S(this.fontSize*screenState.transitionProgress)}px ${this.font}`;
    screenState.canvasCtx.textAlign = 'center';
    screenState.canvasCtx.textBaseline = 'middle';
  }

  /**
  * Renders the drawable.
  * @param {ScreenState} screenState The current state of the screen
  */
  render(screenState) {
    super.render(screenState);
    const xPos = screenState.X(this.x);
    const yPos = screenState.Y(this.y);
    const radius = screenState.S(this.radius*screenState.transitionProgress);
    const inner_radius = screenState.S((this.radius-2)*screenState.transitionProgress);
    screenState.canvasCtx.beginPath();
    screenState.canvasCtx.arc(xPos, yPos, radius, 0,2*Math.PI);
    screenState.canvasCtx.fillStyle = this.hover_progress > 0 ? rgb(108, 212, 255) : rgb(15,223,23);
    screenState.canvasCtx.fill();

    screenState.canvasCtx.setLineDash([screenState.S(8), screenState.S(4)]);
    screenState.canvasCtx.lineDashOffset = -(screenState.animationStep/50 % screenState.S(12));
    screenState.canvasCtx.lineWidth = screenState.S(3);
    screenState.canvasCtx.beginPath();
    screenState.canvasCtx.arc(xPos, yPos, inner_radius, 0,2*Math.PI);
    screenState.canvasCtx.shadowBlur = 15;
    screenState.canvasCtx.shadowColor = rgb(228, 255, 26);
    screenState.canvasCtx.strokeStyle = rgb(228, 255, 26);
    screenState.canvasCtx.stroke();
    screenState.canvasCtx.shadowBlur = 0;

    screenState.canvasCtx.setLineDash([]);
    screenState.canvasCtx.lineDashOffset = 0;
    // screenState.canvasCtx.lineWidth = 3;
    screenState.canvasCtx.beginPath();
    screenState.canvasCtx.arc(xPos, yPos, inner_radius, -Math.PI/2,(2*Math.PI*this.hover_progress)-Math.PI/2);
    screenState.canvasCtx.stroke()

    if (this.loading){
      const spinnerOffset = (screenState.animationStep/1000 % 2000)*2*Math.PI 
      screenState.canvasCtx.beginPath()
      screenState.canvasCtx.strokeStyle = rgba(255,255,22, screenState.transitionProgress);
      screenState.canvasCtx.arc(xPos, yPos, inner_radius/2, spinnerOffset, (0.5*Math.PI)+ spinnerOffset);
      screenState.canvasCtx.stroke()
    }
    else{
      this.icon.render(screenState.canvasCtx, xPos, yPos, radius*2, radius*2, rgba(255,255,22, screenState.transitionProgress));
    }

    screenState.canvasCtx.fillStyle = "black"
    screenState.canvasCtx.fillText(this.text, xPos, yPos + radius + this.fontSize);
  }
}